import { Icon, type LucideIcon } from './Icon';

interface EmptyStateAction {
  label: string;
  onClick: () => void;
  icon?: LucideIcon;
}

interface Props {
  icon: LucideIcon;
  title: string;
  body?: string;
  /** Omit for read-only viewers: an empty screen with a button they can't use is worse than none. */
  action?: EmptyStateAction;
  secondaryAction?: EmptyStateAction;
  /** `page` fills a tab; `inline` sits inside a list, a section or under a filter. */
  size?: 'page' | 'inline';
  className?: string;
}

export default function EmptyState({
  icon, title, body, action, secondaryAction, size = 'page', className = '',
}: Props) {
  const page = size === 'page';
  return (
    <div
      className={`flex flex-col items-center justify-center text-center px-4 ${
        page ? 'py-16 min-h-[320px]' : 'py-8'
      } ${className}`}
    >
      <div
        className={`flex items-center justify-center rounded-2xl bg-teal-50 dark:bg-teal-900/30 ${
          page ? 'w-14 h-14 mb-4' : 'w-10 h-10 mb-3'
        }`}
      >
        <Icon as={icon} size={page ? 24 : 18} className="text-teal-500" />
      </div>
      <p
        className={`font-semibold text-slate-800 dark:text-slate-200 ${page ? 'text-base' : 'text-sm'}`}
      >
        {title}
      </p>
      {body && (
        <p
          className={`text-slate-500 dark:text-slate-400 leading-relaxed mt-1 max-w-sm ${
            page ? 'text-sm' : 'text-xs'
          }`}
        >
          {body}
        </p>
      )}
      {(action || secondaryAction) && (
        <div className="flex flex-wrap items-center justify-center gap-2 mt-5">
          {action && (
            <button type="button" onClick={action.onClick} className="btn-primary gap-2">
              {action.icon && <Icon as={action.icon} size={16} />}
              {action.label}
            </button>
          )}
          {secondaryAction && (
            <button type="button" onClick={secondaryAction.onClick} className="btn-secondary gap-2">
              {secondaryAction.icon && <Icon as={secondaryAction.icon} size={16} />}
              {secondaryAction.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
